import { useEffect, useState } from "react";
import { Youtube, Loader2, Radio, CheckCircle2, Link as LinkIcon, ExternalLink, Tv, Rss, Calendar, Plus, Trash2, Save, Inbox, Mail, Trash } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import KingdomHourAdmin from "@/components/admin/KingdomHourAdmin";

// Admin panel section for HBTV — House of Blessing TV.
// Two capabilities in one place:
//   1) Update channel settings (YouTube channel URL, timezone, marquee, Live toggle)
//   2) Import videos from any public YouTube channel URL/handle into a specific HBTV show
// No YouTube Data API key required — we use YouTube's public Atom RSS feed under the hood.
export default function HBTVAdmin() {
  const [settings, setSettings] = useState(null);
  const [shows, setShows] = useState([]);
  const [busy, setBusy] = useState(false);

  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState({
    youtube_channel_url: "",
    youtube_auto_import_show: "",
    timezone_label: "WAT · Cameroon",
    marquee_message: "",
    live_active: false,
    live_url: "",
    live_title: "",
  });

  // Import form
  const [importForm, setImportForm] = useState({ show_slug: "", channel_url: "", max_videos: 15 });
  const [lastImport, setLastImport] = useState(null);

  // Live schedule state
  const [liveSchedule, setLiveSchedule] = useState([]);
  const [newProg, setNewProg] = useState({
    programme_name: "", description: "",
    day_of_week: -1, hour_wat: 6, minute_wat: 0, duration_minutes: 60, order: 0, active: true,
  });
  const [savingProg, setSavingProg] = useState(false);

  // Enquiry inbox state
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(true);
  const [autoImportBusy, setAutoImportBusy] = useState(false);

  // Chat moderation state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const DAY_OPTS = [
    { v: -1, l: "Every day" },
    { v: 0, l: "Monday" }, { v: 1, l: "Tuesday" }, { v: 2, l: "Wednesday" },
    { v: 3, l: "Thursday" }, { v: 4, l: "Friday" }, { v: 5, l: "Saturday" }, { v: 6, l: "Sunday" },
  ];

  useEffect(() => {
    Promise.all([
      api.get("/tv/settings"),
      api.get("/tv/shows"),
      api.get("/tv/live-schedule"),
      api.get("/admin/hbtv/enquiries"),
      api.get("/tv/admin/chat?limit=100"),
    ]).then(([s, sh, ls, en, ch]) => {
      setSettings(s.data);
      setShows(sh.data || []);
      setLiveSchedule(ls.data || []);
      setEnquiries(en.data || []);
      setEnquiriesLoading(false);
      setChatMessages(ch.data || []);
      setChatLoading(false);
      setForm((f) => ({
        ...f,
        youtube_channel_url: s.data.youtube_channel_url || "",
        youtube_auto_import_show: s.data.youtube_auto_import_show || "",
        timezone_label: s.data.timezone_label || "WAT · Cameroon",
        marquee_message: s.data.marquee_message || "",
        live_active: !!s.data.live_active,
        live_url: s.data.live_url || "",
        live_title: s.data.live_title || "",
      }));
      if (sh.data?.length) setImportForm((f) => ({ ...f, show_slug: sh.data[0].slug }));
    }).catch(() => {});
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/tv/admin/settings", {
        channel_name: settings?.channel_name || "HBTV",
        channel_full_name: settings?.channel_full_name || "House of Blessing TV",
        tagline: settings?.tagline || "Faith · Leadership · Business · Innovation",
        marquee_message: form.marquee_message,
        live_active: form.live_active,
        live_url: form.live_url,
        live_title: form.live_title,
        timezone_label: form.timezone_label,
        youtube_channel_url: form.youtube_channel_url,
        youtube_auto_import_show: form.youtube_auto_import_show,
      });
      setSettings(data);
      toast.success("Channel settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const runImport = async (e) => {
    e.preventDefault();
    if (!importForm.channel_url.trim() || !importForm.show_slug) {
      toast.error("Choose a show and paste a YouTube channel URL");
      return;
    }
    setBusy(true);
    setLastImport(null);
    try {
      const { data } = await api.post("/tv/admin/import-youtube", {
        show_slug: importForm.show_slug,
        channel_url: importForm.channel_url.trim(),
        max_videos: Number(importForm.max_videos) || 15,
      });
      setLastImport(data);
      toast.success(`Imported ${data.created} new episode${data.created === 1 ? "" : "s"} into ${importForm.show_slug}`);
      // Refresh channel URL field from server-side sync
      try {
        const { data: s } = await api.get("/tv/settings");
        setSettings(s);
        setForm((f) => ({ ...f, youtube_channel_url: s.youtube_channel_url || f.youtube_channel_url }));
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runAutoImport = async () => {
    setAutoImportBusy(true);
    try {
      const { data } = await api.post("/admin/hbtv/youtube-auto-import/run");
      if (data.status === "skipped") {
        toast.error("Auto-import is not configured. Pick a target show and save first.");
      } else if (data.status === "error") {
        toast.error(data.message || "Auto-import failed");
      } else {
        toast.success(`Auto-import complete · ${data.created} new · ${data.skipped_already_imported} skipped`);
        try {
          const { data: s } = await api.get("/tv/settings");
          setSettings(s);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAutoImportBusy(false);
    }
  };

  const deleteChatMessage = async (id) => {
    if (!window.confirm("Delete this chat message? Viewers won't see it anymore.")) return;
    try {
      await api.delete(`/tv/admin/chat/${id}`);
      setChatMessages((prev) => prev.map((m) => (m.id === id ? { ...m, deleted: true } : m)));
      toast.success("Message removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reloadSchedule = async () => {
    try {
      const { data } = await api.get("/tv/live-schedule");
      setLiveSchedule(data || []);
    } catch { /* ignore */ }
  };

  const addProgramme = async (e) => {
    e.preventDefault();
    if (!newProg.programme_name.trim()) {
      toast.error("Programme name required");
      return;
    }
    setSavingProg(true);
    try {
      await api.post("/tv/admin/live-schedule", {
        ...newProg,
        hour_wat: Number(newProg.hour_wat),
        minute_wat: Number(newProg.minute_wat),
        duration_minutes: Number(newProg.duration_minutes),
        day_of_week: Number(newProg.day_of_week),
      });
      toast.success("Programme added");
      setNewProg({ programme_name: "", description: "", day_of_week: -1, hour_wat: 6, minute_wat: 0, duration_minutes: 60, order: 0, active: true });
      reloadSchedule();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProg(false);
    }
  };

  const updateProgramme = async (prog) => {
    try {
      await api.patch(`/tv/admin/live-schedule/${prog.id}`, {
        programme_name: prog.programme_name,
        description: prog.description || "",
        day_of_week: Number(prog.day_of_week),
        hour_wat: Number(prog.hour_wat),
        minute_wat: Number(prog.minute_wat),
        duration_minutes: Number(prog.duration_minutes),
        order: Number(prog.order || 0),
        active: !!prog.active,
      });
      toast.success("Saved");
      reloadSchedule();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteProgramme = async (id) => {
    if (!window.confirm("Delete this programme?")) return;
    try {
      await api.delete(`/tv/admin/live-schedule/${id}`);
      toast.success("Programme removed");
      reloadSchedule();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10" data-testid="hbtv-admin">
      {/* Kingdom Hour — 12-part series manager (video uploads) */}
      <KingdomHourAdmin />

      {/* Setup guide */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-setup-guide">
        <div className="flex items-center gap-3 mb-4">
          <Youtube size={22} className="text-red-400" />
          <h3 className="font-serif text-2xl text-white">Set up your YouTube channel</h3>
        </div>
        <p className="text-white/65 text-sm leading-relaxed mb-5">
          Every video the ministry uploads to YouTube can appear on HBTV within seconds — no re-uploading. Follow these one-time steps:
        </p>
        <ol className="space-y-3 text-sm text-white/80 leading-relaxed list-decimal list-inside marker:text-gold-500">
          <li>
            Sign in to <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="text-gold-300 hover:text-gold-200 underline">youtube.com</a> with the ministry's Google account (create a free one if needed).
          </li>
          <li>
            Click your profile picture → <span className="text-gold-300">Create a channel</span>. Name it <span className="font-semibold text-white">"House of Blessing"</span> and pick a handle like <span className="font-mono text-gold-300">@houseofblessing</span>.
          </li>
          <li>
            Upload sermons, worship sets, and interviews at <a href="https://studio.youtube.com" target="_blank" rel="noopener noreferrer" className="text-gold-300 hover:text-gold-200 underline">studio.youtube.com</a>. Even 3 videos are enough to start.
          </li>
          <li>
            Come back to this page, paste your channel URL below, and hit <span className="text-gold-300 font-semibold">Import</span>. HBTV will pull them into the chosen show automatically.
          </li>
        </ol>
        <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-white/40">
          Zero YouTube API key needed · Uses YouTube's public RSS feed
        </p>
      </section>

      {/* Channel settings */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-settings">
        <div className="flex items-center gap-3 mb-5">
          <Tv size={22} className="text-gold-500" />
          <h3 className="font-serif text-2xl text-white">Channel settings</h3>
        </div>
        <form onSubmit={saveSettings} className="grid gap-4 md:grid-cols-2">
          <label className="col-span-2 text-xs uppercase tracking-[0.2em] text-white/60">
            <span>YouTube channel URL (or @handle)</span>
            <input
              type="text"
              value={form.youtube_channel_url}
              onChange={(e) => setForm({ ...form, youtube_channel_url: e.target.value })}
              placeholder="https://www.youtube.com/@houseofblessing"
              data-testid="hbtv-yt-url"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm normal-case px-3 py-2.5 outline-none tracking-normal"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Broadcast timezone label</span>
            <input
              type="text"
              value={form.timezone_label}
              onChange={(e) => setForm({ ...form, timezone_label: e.target.value })}
              placeholder="WAT · Cameroon"
              data-testid="hbtv-tz"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm normal-case px-3 py-2.5 outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Marquee message</span>
            <input
              type="text"
              value={form.marquee_message}
              onChange={(e) => setForm({ ...form, marquee_message: e.target.value })}
              placeholder="Broadcasting Kingdom content 24/7 …"
              data-testid="hbtv-marquee"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm normal-case px-3 py-2.5 outline-none"
            />
          </label>
          <label className="col-span-2 text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Auto-import target show (runs nightly at 04:00 UTC)</span>
            <select
              value={form.youtube_auto_import_show}
              onChange={(e) => setForm({ ...form, youtube_auto_import_show: e.target.value })}
              data-testid="hbtv-auto-import-show"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm normal-case px-3 py-2.5 outline-none"
            >
              <option value="">— Disabled (no nightly import) —</option>
              {shows.map((s) => (
                <option key={s.slug} value={s.slug}>{s.title}</option>
              ))}
            </select>
            {settings?.youtube_last_auto_import_at && (
              <p className="mt-2 text-[10px] text-white/40 normal-case tracking-normal" data-testid="hbtv-last-auto-import">
                Last run · {new Date(settings.youtube_last_auto_import_at).toLocaleString()} · added {settings.youtube_last_auto_import_created || 0} new episode(s)
              </p>
            )}
          </label>
          <div className="col-span-2 border-t border-gold-500/15 pt-4 mt-2">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-500 mb-3 flex items-center gap-2"><Radio size={12} /> Go Live</p>
            <label className="flex items-center gap-3 text-sm text-white/85 mb-3">
              <input
                type="checkbox"
                checked={form.live_active}
                onChange={(e) => setForm({ ...form, live_active: e.target.checked })}
                data-testid="hbtv-live-toggle"
                className="w-4 h-4 accent-gold-500"
              />
              Broadcast live now (overrides the schedule on /tv)
            </label>
            <div className="grid gap-3 md:grid-cols-2">
              <input
                type="text"
                value={form.live_url}
                onChange={(e) => setForm({ ...form, live_url: e.target.value })}
                placeholder="YouTube Live URL"
                data-testid="hbtv-live-url"
                className="bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm px-3 py-2.5 outline-none"
              />
              <input
                type="text"
                value={form.live_title}
                onChange={(e) => setForm({ ...form, live_title: e.target.value })}
                placeholder="Live event title (e.g. Sunday Service)"
                data-testid="hbtv-live-title"
                className="bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm px-3 py-2.5 outline-none"
              />
            </div>
          </div>
          <div className="col-span-2 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={savingSettings}
              data-testid="hbtv-save-settings"
              className="inline-flex items-center gap-2 bg-gold-500 text-ink font-semibold uppercase tracking-[0.14em] text-xs px-6 py-3 hover:bg-gold-400 disabled:opacity-60 transition-colors"
            >
              {savingSettings ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : "Save settings"}
            </button>
            <button
              type="button"
              onClick={runAutoImport}
              disabled={autoImportBusy || !form.youtube_auto_import_show || !form.youtube_channel_url}
              data-testid="hbtv-run-auto-import"
              title={!form.youtube_auto_import_show ? "Pick a target show and save first" : "Import latest videos now"}
              className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 uppercase tracking-[0.14em] text-xs px-5 py-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {autoImportBusy ? (<><Loader2 size={14} className="animate-spin" /> Importing…</>) : (<><Youtube size={14} /> Run auto-import now</>)}
            </button>
          </div>
        </form>
      </section>

      {/* Import from YouTube */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-import">
        <div className="flex items-center gap-3 mb-5">
          <Rss size={22} className="text-red-400" />
          <h3 className="font-serif text-2xl text-white">Import videos from YouTube</h3>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-5">
          Paste the ministry's YouTube channel URL (or a handle like <span className="font-mono text-gold-300">@houseofblessing</span>) and choose which HBTV show to append them to. We'll pull the latest 15 videos, skip anything already imported, and attach them as playable episodes.
        </p>
        <form onSubmit={runImport} className="grid gap-4 md:grid-cols-[1fr_240px_180px_auto] md:items-end">
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">
            <span>YouTube channel URL or handle</span>
            <input
              type="text"
              value={importForm.channel_url}
              onChange={(e) => setImportForm({ ...importForm, channel_url: e.target.value })}
              placeholder="https://www.youtube.com/@houseofblessing"
              required
              data-testid="hbtv-import-url"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm normal-case px-3 py-2.5 outline-none"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Import into show</span>
            <select
              value={importForm.show_slug}
              onChange={(e) => setImportForm({ ...importForm, show_slug: e.target.value })}
              data-testid="hbtv-import-show"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm px-3 py-2.5 outline-none"
            >
              {shows.map((s) => (
                <option key={s.slug} value={s.slug}>{s.title}</option>
              ))}
            </select>
          </label>
          <label className="text-xs uppercase tracking-[0.2em] text-white/60">
            <span>Max videos</span>
            <input
              type="number"
              min={1}
              max={50}
              value={importForm.max_videos}
              onChange={(e) => setImportForm({ ...importForm, max_videos: e.target.value })}
              data-testid="hbtv-import-max"
              className="mt-2 w-full bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm px-3 py-2.5 outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="hbtv-import-submit"
            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold uppercase tracking-[0.14em] text-xs px-6 py-3 disabled:opacity-60 transition-colors h-fit"
          >
            {busy ? (<><Loader2 size={14} className="animate-spin" /> Importing…</>) : (<><Youtube size={14} /> Import</>)}
          </button>
        </form>

        {lastImport && (
          <div className="mt-5 border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-white/85" data-testid="hbtv-import-result">
            <div className="flex items-center gap-2 mb-2 text-emerald-300">
              <CheckCircle2 size={16} /> Import complete
            </div>
            <ul className="space-y-1 text-xs uppercase tracking-[0.14em] text-white/70">
              <li>Channel: <span className="text-white font-mono normal-case">{lastImport.channel_id}</span> {lastImport.handle && <span className="text-gold-300">@{lastImport.handle}</span>}</li>
              <li>Created: <span className="text-emerald-300">{lastImport.created}</span> · Skipped (already imported): <span className="text-white/50">{lastImport.skipped_already_imported}</span></li>
              <li>Target show: <span className="text-gold-300 normal-case">{lastImport.target_show}</span></li>
            </ul>
            <a
              href={`https://www.youtube.com/channel/${lastImport.channel_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-gold-300 hover:text-gold-200"
            >
              Open channel on YouTube <ExternalLink size={12} />
            </a>
          </div>
        )}
      </section>

      {/* Live Stream Schedule editor */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-live-schedule">
        <div className="flex items-center gap-3 mb-5">
          <Calendar size={22} className="text-gold-500" />
          <h3 className="font-serif text-2xl text-white">Live Stream Schedule</h3>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-5">
          Manage the programmes shown on the <a href="/tv" target="_blank" rel="noopener noreferrer" className="text-gold-300 hover:text-gold-200 underline">HBTV landing page</a>. Times are stored in <span className="text-gold-300">WAT (UTC+1)</span> and auto-converted to each visitor's local timezone. The countdown timer picks the soonest upcoming programme.
        </p>

        {/* Existing programmes */}
        <div className="mb-6 border border-gold-500/15 divide-y divide-gold-500/10" data-testid="hbtv-schedule-list">
          {liveSchedule.length === 0 && (
            <p className="p-5 text-white/50 text-sm">No programmes yet — add your first below.</p>
          )}
          {liveSchedule.map((p) => (
            <ProgrammeRow key={p.id} prog={p} onSave={updateProgramme} onDelete={deleteProgramme} DAY_OPTS={DAY_OPTS} />
          ))}
        </div>

        {/* Add new */}
        <form onSubmit={addProgramme} className="grid gap-3 md:grid-cols-6" data-testid="hbtv-schedule-add">
          <input required placeholder="Programme name" value={newProg.programme_name} onChange={(e) => setNewProg({ ...newProg, programme_name: e.target.value })} className="md:col-span-2 bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm px-3 py-2.5 outline-none" />
          <input placeholder="Short description" value={newProg.description} onChange={(e) => setNewProg({ ...newProg, description: e.target.value })} className="md:col-span-2 bg-ink border border-gold-500/25 focus:border-gold-400 text-white placeholder:text-white/35 text-sm px-3 py-2.5 outline-none" />
          <select value={newProg.day_of_week} onChange={(e) => setNewProg({ ...newProg, day_of_week: e.target.value })} className="bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm px-3 py-2.5 outline-none">
            {DAY_OPTS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <input type="number" min={0} max={23} placeholder="H" value={newProg.hour_wat} onChange={(e) => setNewProg({ ...newProg, hour_wat: e.target.value })} className="w-16 bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm px-2 py-2.5 outline-none text-center" />
            <span className="text-white/50">:</span>
            <input type="number" min={0} max={59} placeholder="M" value={newProg.minute_wat} onChange={(e) => setNewProg({ ...newProg, minute_wat: e.target.value })} className="w-16 bg-ink border border-gold-500/25 focus:border-gold-400 text-white text-sm px-2 py-2.5 outline-none text-center" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold-500">WAT</span>
          </div>
          <button type="submit" disabled={savingProg} data-testid="hbtv-schedule-add-submit" className="md:col-span-6 inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-xs px-6 py-3 disabled:opacity-60 transition-colors">
            {savingProg ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add programme
          </button>
        </form>
      </section>

      {/* Enquiry Inbox */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-inbox">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Inbox size={22} className="text-gold-500" />
            <h3 className="font-serif text-2xl text-white">Advertising Enquiries <span className="text-white/40 text-sm ml-2">({enquiries.length})</span></h3>
          </div>
          <button
            onClick={async () => { try { const { data } = await api.get("/admin/hbtv/enquiries"); setEnquiries(data || []); toast.success("Refreshed"); } catch { /* */ } }}
            className="text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300"
            data-testid="hbtv-inbox-refresh"
          >
            Refresh
          </button>
        </div>
        {enquiriesLoading ? (
          <div className="py-10 text-center text-white/50"><Loader2 size={18} className="inline animate-spin mr-2" /> Loading…</div>
        ) : enquiries.length === 0 ? (
          <p className="py-10 text-center text-white/50 text-sm">No advertising enquiries yet.</p>
        ) : (
          <ul className="space-y-3">
            {enquiries.map((en) => (
              <li key={en.id} className={`border ${en.read ? "border-gold-500/15 bg-ink/30" : "border-gold-500/40 bg-gold-500/5"} p-4`} data-testid={`hbtv-enquiry-${en.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.24em] text-gold-400">{en.package}</span>
                      {!en.read && <span className="text-[9px] uppercase tracking-[0.24em] bg-gold-500 text-ink px-2 py-0.5">New</span>}
                      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{(en.created_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="mt-2 font-serif text-lg text-white">{en.name} {en.organization && <span className="text-white/50 text-sm">· {en.organization}</span>}</p>
                    <p className="mt-0.5 text-xs text-white/60">{en.email} · {en.phone} {en.preferred_date && <> · Preferred: <span className="text-gold-300">{en.preferred_date}</span></>}</p>
                    <p className="mt-3 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{en.message}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`mailto:${en.email}?subject=${encodeURIComponent("Re: HBTV — " + en.package)}&body=${encodeURIComponent("Hello " + en.name + ",\n\nThank you for your enquiry regarding " + en.package + " on HBTV.\n\n")}`}
                      className="inline-flex items-center gap-1.5 border border-gold-500/40 hover:border-gold-400 text-gold-300 hover:text-gold-200 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors"
                      data-testid={`hbtv-enquiry-reply-${en.id}`}
                    >
                      <Mail size={12} /> Reply
                    </a>
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.patch(`/admin/hbtv/enquiries/${en.id}`, { read: !en.read });
                          setEnquiries((es) => es.map((x) => (x.id === en.id ? data : x)));
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="inline-flex items-center gap-1.5 border border-white/20 hover:border-gold-400 text-white/70 hover:text-gold-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors"
                      data-testid={`hbtv-enquiry-toggle-${en.id}`}
                    >
                      {en.read ? "Mark unread" : "Mark read"}
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete this enquiry?")) return;
                        try { await api.delete(`/admin/hbtv/enquiries/${en.id}`); setEnquiries((es) => es.filter((x) => x.id !== en.id)); toast.success("Deleted"); }
                        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="p-1.5 border border-red-500/30 hover:border-red-400 text-red-300 hover:text-red-100 transition-colors"
                      data-testid={`hbtv-enquiry-delete-${en.id}`}
                      aria-label="Delete"
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Live Chat Moderation */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-chat-mod">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Inbox size={22} className="text-gold-500" />
            <h3 className="font-serif text-2xl text-white">
              Live Chat Moderation
              <span className="text-white/40 text-sm ml-2">
                ({chatMessages.filter((m) => !m.deleted).length} visible)
              </span>
            </h3>
          </div>
          <button
            onClick={async () => {
              try {
                const { data } = await api.get("/tv/admin/chat?limit=100");
                setChatMessages(data || []);
                toast.success("Refreshed");
              } catch { /* */ }
            }}
            data-testid="hbtv-chat-mod-refresh"
            className="text-[11px] uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300"
          >
            Refresh
          </button>
        </div>
        {chatLoading ? (
          <p className="py-10 text-center text-white/50 text-sm">Loading…</p>
        ) : chatMessages.length === 0 ? (
          <p className="py-10 text-center text-white/50 text-sm">No chat messages yet.</p>
        ) : (
          <ul className="divide-y divide-gold-500/10 max-h-[420px] overflow-y-auto" data-testid="hbtv-chat-mod-list">
            {chatMessages.map((m) => (
              <li key={m.id} className={`py-3 flex items-start gap-3 ${m.deleted ? "opacity-40" : ""}`} data-testid="hbtv-chat-mod-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gold-300">
                      {m.name}
                    </span>
                    {m.is_prayer && (
                      <span className="text-[9px] uppercase tracking-[0.18em] bg-gold-500/15 text-gold-300 px-1.5 py-0.5">Prayer</span>
                    )}
                    {m.deleted && (
                      <span className="text-[9px] uppercase tracking-[0.18em] bg-red-500/15 text-red-300 px-1.5 py-0.5">Removed</span>
                    )}
                    <span className="text-[9px] text-white/40 ml-auto">
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-white/80 mt-1 break-words">{m.message}</p>
                </div>
                {!m.deleted && (
                  <button
                    onClick={() => deleteChatMessage(m.id)}
                    data-testid={`hbtv-chat-mod-delete-${m.id}`}
                    aria-label="Remove message"
                    className="p-2 border border-red-500/30 hover:border-red-400 text-red-400 hover:text-red-300 transition-colors shrink-0"
                  >
                    <Trash size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Quick links */}
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6" data-testid="hbtv-quicklinks">
        <div className="flex items-center gap-3 mb-4">
          <LinkIcon size={20} className="text-gold-500" />
          <h3 className="font-serif text-2xl text-white">Quick links</h3>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <a href="/tv" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">HBTV landing page →</a>
          <a href="/tv/live" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">Live 24/7 player →</a>
          <a href="/tv/advertise" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">Advertise on HBTV →</a>
          <a href="/api/hbtv/ratecard.pdf" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">Download Rate Card PDF →</a>
          <a href="/api/hbtv/podcast.rss" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">Podcast RSS feed →</a>
          <a href="https://studio.youtube.com" target="_blank" rel="noopener noreferrer" className="border border-gold-500/25 hover:border-gold-400 p-4 text-white/80 hover:text-gold-300 transition-colors text-sm">YouTube Studio (upload) →</a>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------
// Editable programme row
// -----------------------------------------------------------------------
function ProgrammeRow({ prog, onSave, onDelete, DAY_OPTS }) {
  const [draft, setDraft] = useState(prog);
  const dirty = JSON.stringify(draft) !== JSON.stringify(prog);
  return (
    <div className="grid gap-2 md:grid-cols-[1.6fr_1.8fr_1fr_1fr_auto] items-center px-4 py-3" data-testid={`hbtv-schedule-row-${prog.id}`}>
      <input value={draft.programme_name} onChange={(e) => setDraft({ ...draft, programme_name: e.target.value })} className="bg-transparent border-b border-white/10 focus:border-gold-400 text-white text-sm py-1 outline-none" />
      <input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Description" className="bg-transparent border-b border-white/10 focus:border-gold-400 text-white/70 text-xs py-1 outline-none" />
      <select value={draft.day_of_week} onChange={(e) => setDraft({ ...draft, day_of_week: Number(e.target.value) })} className="bg-ink border border-gold-500/20 text-white text-xs px-2 py-1.5 outline-none">
        {DAY_OPTS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
      </select>
      <div className="flex items-center gap-1">
        <input type="number" min={0} max={23} value={draft.hour_wat} onChange={(e) => setDraft({ ...draft, hour_wat: Number(e.target.value) })} className="w-12 bg-ink border border-gold-500/20 text-white text-xs px-1 py-1.5 text-center outline-none" />
        <span className="text-white/40 text-xs">:</span>
        <input type="number" min={0} max={59} value={draft.minute_wat} onChange={(e) => setDraft({ ...draft, minute_wat: Number(e.target.value) })} className="w-12 bg-ink border border-gold-500/20 text-white text-xs px-1 py-1.5 text-center outline-none" />
        <span className="text-[9px] uppercase tracking-[0.16em] text-gold-500 ml-1">WAT</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSave(draft)}
          disabled={!dirty}
          data-testid={`hbtv-schedule-save-${prog.id}`}
          className="p-1.5 border border-gold-500/30 text-gold-300 hover:text-gold-100 hover:border-gold-400 disabled:opacity-40 transition-colors"
          aria-label="Save"
        >
          <Save size={13} />
        </button>
        <button
          onClick={() => onDelete(prog.id)}
          data-testid={`hbtv-schedule-delete-${prog.id}`}
          className="p-1.5 border border-red-500/30 text-red-300 hover:text-red-100 hover:border-red-400 transition-colors"
          aria-label="Delete"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
